import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { 
  PlusIcon, 
  TrashIcon, 
  PlayIcon,
  DocumentDuplicateIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import { RULE_CATEGORIES, CATEGORIES, CATEGORY_GROUPS } from '../../config/categories'
import { getLocalApiUrl } from '../../config/api'
import { TYPOLOGY_KEYS, TYPOLOGIES } from '../compliance-safe/typologyData/typologies'

// API Configuration

const RULE_TYPES = [
  { value: 'transaction', label: 'Transaction Rule', description: 'Triggered by individual transactions' },
  { value: 'session', label: 'Session Rule', description: 'Triggered by gaming sessions' },
  { value: 'onboarding', label: 'Onboarding Rule', description: 'Triggered by member registration' },
  { value: 'ongoing', label: 'Ongoing Rule', description: 'Triggered by events (eKYC, tier upgrades)' }
]

const PROCESSING_CATEGORIES = [
  { value: 'session-based', label: 'Session-based', description: 'Analyze single record only' },
  { value: 'cumulative', label: 'Cumulative', description: 'Analyze sequences/aggregations over time periods' }
]

// Categories are now imported from config/categories.js

const SEVERITY_LEVELS = [
  { value: 'LOW', label: 'Low', color: 'green' },
  { value: 'MEDIUM', label: 'Medium', color: 'yellow' },
  { value: 'HIGH', label: 'High', color: 'orange' },
  { value: 'CRITICAL', label: 'Critical', color: 'red' }
]

const OPERATORS = [
  { value: 'equals', label: 'Equals (=)' },
  { value: 'not_equals', label: 'Not Equals (≠)' },
  { value: 'greater_than', label: 'Greater Than (>)' },
  { value: 'greater_than_or_equal', label: 'Greater Than or Equal (≥)' },
  { value: 'less_than', label: 'Less Than (<)' },
  { value: 'less_than_or_equal', label: 'Less Than or Equal (≤)' },
  { value: 'between', label: 'Between' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In List' }
]

const TRANSACTION_FIELDS = [
  // Cash & Money Fields
  { value: 'CashIn', label: 'Cash In Amount', type: 'number' },
  { value: 'CashDrop', label: 'Cash Drop Amount', type: 'number' },
  { value: 'CoinOut', label: 'Coin Out Amount', type: 'number' },
  { value: 'CashlessIn', label: 'Cashless In Amount', type: 'number' },
  { value: 'CashlessOut', label: 'Cashless Out Amount', type: 'number' },
  
  // Bill Denominations
  { value: 'Bills', label: 'Total Bills', type: 'number' },
  { value: 'Bill10', label: '$10 Bills', type: 'number' },
  { value: 'Bill20', label: '$20 Bills', type: 'number' },
  { value: 'Bill50', label: '$50 Bills', type: 'number' },
  { value: 'Bill100', label: '$100 Bills', type: 'number' },
  
  // Gaming Fields
  { value: 'Turnover', label: 'Turnover Amount', type: 'number' },
  { value: 'CreditWin', label: 'Credit Win Amount', type: 'number' },
  { value: 'CancelCredit', label: 'Cancel Credit Amount', type: 'number' },
  { value: 'Credit', label: 'Current Credit Balance', type: 'number' },
  { value: 'Stroke', label: 'Number of Plays/Spins', type: 'number' },
  
  // Ticket Fields
  { value: 'TicketsIn', label: 'Tickets In', type: 'number' },
  { value: 'TicketOut', label: 'Tickets Out', type: 'number' },
  
  // Identification Fields
  { value: 'MemberID', label: 'Player ID', type: 'string' },
  { value: 'EgmHouseNo', label: 'EGM House Number', type: 'string' },
  { value: 'VenueCode', label: 'Venue Code', type: 'string' },
  
  // Timing Fields
  { value: 'SnapshotTime', label: 'Snapshot Time', type: 'datetime' },
  { value: 'GamingDate', label: 'Gaming Date', type: 'date' },
  { value: 'MeterReadTime', label: 'Meter Read Time', type: 'datetime' }
]

const SESSION_FIELDS = [
  // Session Identification
  { value: 'SessionPK', label: 'Session Primary Key', type: 'string' },
  { value: 'MemberID', label: 'Player ID', type: 'string' },
  
  // EGM Information
  { value: 'EgmName', label: 'EGM Name', type: 'string' },
  { value: 'EgmHouseNo', label: 'EGM House Number', type: 'string' },
  { value: 'GameVariationID', label: 'Game Variation ID', type: 'string' },
  { value: 'Venue', label: 'Venue', type: 'string' },
  
  // Session Timing
  { value: 'StartTime', label: 'Session Start Time', type: 'datetime' },
  { value: 'EndTime', label: 'Session End Time', type: 'datetime' },
  { value: 'GamingDate', label: 'Gaming Date', type: 'date' },
  { value: 'TxDate', label: 'Transaction Date', type: 'date' },
  
  // Session Totals
  { value: 'Turnover', label: 'Session Turnover', type: 'number' },
  { value: 'Stroke', label: 'Total Plays/Spins', type: 'number' },
  { value: 'CreditWin', label: 'Total Credit Won', type: 'number' },
  
  // Calculated Session Fields
  { value: 'SessionDuration', label: 'Session Duration (minutes)', type: 'number' },
  { value: 'AvgBetSize', label: 'Average Bet Size', type: 'number' },
  { value: 'WinLossRatio', label: 'Win/Loss Ratio', type: 'number' }
]

const PLAYER_PROFILE_FIELDS = [
  { value: 'profile.age', label: 'Player Age', type: 'number' },
  { value: 'profile.type', label: 'Profile Type (Member/Visitor)', type: 'string' },
  { value: 'profile.isMember', label: 'Is Member', type: 'boolean' },
  { value: 'profile.isVisitor', label: 'Is Visitor', type: 'boolean' },
  { value: 'profile.riskScore', label: 'Calculated Risk Score', type: 'number' },
  { value: 'profile.membershipTier', label: 'Membership Tier', type: 'string' },
  { value: 'profile.registrationDate', label: 'Registration Date', type: 'date' }
]

const GAMING_MACHINE_FIELDS = [
  { value: 'egm.machineId', label: 'Gaming Machine ID', type: 'string' },
  { value: 'egm.machineType', label: 'Machine Type', type: 'string' },
  { value: 'egm.denomination', label: 'Machine Denomination', type: 'number' },
  { value: 'egm.gameTheme', label: 'Game Theme', type: 'string' },
  { value: 'egm.location', label: 'Machine Location', type: 'string' },
  { value: 'egm.zone', label: 'Gaming Zone', type: 'string' }
]

const TIME_OF_DAY_FIELDS = [
  { value: 'timeOfDay.period', label: 'Time Period (Night/Day)', type: 'string' },
  { value: 'timeOfDay.isNight', label: 'Is Night Time (6PM-6AM)', type: 'boolean' },
  { value: 'timeOfDay.isDay', label: 'Is Day Time (6AM-6PM)', type: 'boolean' },
  { value: 'timeOfDay.hour', label: 'Hour of Day (0-23)', type: 'number' }
]

const PERIOD_COMPARISON_FIELDS = [
  // Current Period Fields
  { value: 'periodComparison.current.sessionCount', label: 'Current Period - Session Count', type: 'number' },
  { value: 'periodComparison.current.avgTurnover', label: 'Current Period - Average Turnover', type: 'number' },
  { value: 'periodComparison.current.avgBetSize', label: 'Current Period - Average Bet Size', type: 'number' },
  { value: 'periodComparison.current.avgWinPercentage', label: 'Current Period - Average Win %', type: 'number' },
  { value: 'periodComparison.current.avgSessionDuration', label: 'Current Period - Average Session Duration', type: 'number' },
  { value: 'periodComparison.current.uniqueEGMCount', label: 'Current Period - Unique EGM Count', type: 'number' },
  
  // Previous Period Fields
  { value: 'periodComparison.previous.sessionCount', label: 'Previous Period - Session Count', type: 'number' },
  { value: 'periodComparison.previous.avgTurnover', label: 'Previous Period - Average Turnover', type: 'number' },
  { value: 'periodComparison.previous.avgBetSize', label: 'Previous Period - Average Bet Size', type: 'number' },
  { value: 'periodComparison.previous.avgWinPercentage', label: 'Previous Period - Average Win %', type: 'number' },
  { value: 'periodComparison.previous.avgSessionDuration', label: 'Previous Period - Average Session Duration', type: 'number' },
  { value: 'periodComparison.previous.uniqueEGMCount', label: 'Previous Period - Unique EGM Count', type: 'number' },
  
  // Change Percentage Fields
  { value: 'periodComparison.changes.turnoverChange', label: 'Change in Average Turnover %', type: 'number' },
  { value: 'periodComparison.changes.winPercentageChange', label: 'Change in Average Win %', type: 'number' },
  { value: 'periodComparison.changes.sessionCountChange', label: 'Change in Session Count %', type: 'number' },
  { value: 'periodComparison.changes.avgBetSizeChange', label: 'Change in Average Bet Size %', type: 'number' },
  { value: 'periodComparison.changes.sessionDurationChange', label: 'Change in Session Duration %', type: 'number' }
]

const DEVICE_PATTERN_FIELDS = [
  // Device Count Fields
  { value: 'devicePatterns.uniqueDeviceCount', label: 'Unique Device Count (in time window)', type: 'number' },
  { value: 'devicePatterns.sessionCount', label: 'Session Count (in time window)', type: 'number' },
  { value: 'devicePatterns.totalTurnover', label: 'Total Turnover (in time window)', type: 'number' },
  
  // Device Activity Flags
  { value: 'devicePatterns.hasMultipleDevices', label: 'Has Multiple Devices', type: 'boolean' },
  { value: 'devicePatterns.hasRapidTransitions', label: 'Has Rapid Transitions', type: 'boolean' },
  
  // Transition Tracking
  { value: 'devicePatterns.rapidTransitionCount', label: 'Rapid Transition Count', type: 'number' },
  { value: 'devicePatterns.recentTransitions.length', label: 'Recent Transition Count', type: 'number' },
  
  // Time Window Field (for different windows)
  { value: 'devicePatterns.timeWindow', label: 'Time Window', type: 'string' }
]

const TIME_PATTERN_FIELDS = [
  // Current Session Time Analysis
  { value: 'timePatterns.currentSession.startsAtOpening', label: 'Starts at Venue Opening', type: 'boolean' },
  { value: 'timePatterns.currentSession.endsAtClosing', label: 'Ends at Venue Closing', type: 'boolean' },
  { value: 'timePatterns.currentSession.spansMealTime', label: 'Spans Meal Time', type: 'boolean' },
  { value: 'timePatterns.currentSession.duringMealOnly', label: 'During Meal Time Only', type: 'boolean' },
  { value: 'timePatterns.currentSession.unusualTiming', label: 'Unusual Timing', type: 'boolean' },
  { value: 'timePatterns.currentSession.startHour', label: 'Start Hour', type: 'number' },
  { value: 'timePatterns.currentSession.endHour', label: 'End Hour', type: 'number' },
  { value: 'timePatterns.currentSession.isWeekend', label: 'Is Weekend', type: 'boolean' },
  { value: 'timePatterns.currentSession.startPeriod', label: 'Start Period', type: 'string' },
  { value: 'timePatterns.currentSession.endPeriod', label: 'End Period', type: 'string' },
  
  // Behavioral Pattern Flags
  { value: 'timePatterns.hasOpeningPattern', label: 'Has Opening Pattern (>70%)', type: 'boolean' },
  { value: 'timePatterns.hasClosingPattern', label: 'Has Closing Pattern (>70%)', type: 'boolean' },
  { value: 'timePatterns.hasMealTimePattern', label: 'Has Meal Time Pattern (>50%)', type: 'boolean' },
  { value: 'timePatterns.hasUnusualTimingPattern', label: 'Has Unusual Timing Pattern (>30%)', type: 'boolean' },
  
  // Behavior Statistics
  { value: 'timePatterns.behaviorFlags.frequentOpeningStarterPct', label: 'Opening Starter Percentage', type: 'number' },
  { value: 'timePatterns.behaviorFlags.frequentClosingEnderPct', label: 'Closing Ender Percentage', type: 'number' },
  { value: 'timePatterns.behaviorFlags.mealTimeGamblerPct', label: 'Meal Time Gambler Percentage', type: 'number' },
  { value: 'timePatterns.behaviorFlags.unusualHoursGamblerPct', label: 'Unusual Hours Gambler Percentage', type: 'number' },
  
  // Session Counts
  { value: 'timePatterns.behaviorFlags.frequentOpeningStarter', label: 'Opening Sessions Count', type: 'number' },
  { value: 'timePatterns.behaviorFlags.frequentClosingEnder', label: 'Closing Sessions Count', type: 'number' },
  { value: 'timePatterns.behaviorFlags.mealTimeGambler', label: 'Meal Time Sessions Count', type: 'number' },
  { value: 'timePatterns.behaviorFlags.unusualHoursGambler', label: 'Unusual Hours Sessions Count', type: 'number' }
]

const TIME_WINDOW_OPTIONS = [
  { value: '15min', label: '15 Minutes' },
  { value: '30min', label: '30 Minutes' },
  { value: '1hour', label: '1 Hour' },
  { value: '2hour', label: '2 Hours' },
  { value: '4hour', label: '4 Hours' }
]

const ONGOING_EVENT_TYPES = [
  { value: 'ekyc_request', label: 'eKYC Request', description: 'Enhanced KYC verification request' },
  { value: 'tier_upgrade', label: 'Tier Upgrade', description: 'Customer tier/status upgrade' },
  { value: 'account_review', label: 'Account Review', description: 'Manual account review trigger' },
  { value: 'compliance_check', label: 'Compliance Check', description: 'Periodic compliance verification' }
]

const CUMULATIVE_AGGREGATIONS = [
  { value: 'daily', label: 'Daily Total', description: 'Sum for current day' },
  { value: 'rolling_24h', label: 'Rolling 24 Hours', description: 'Sum for last 24 hours' },
  { value: 'rolling_7d', label: 'Rolling 7 Days', description: 'Sum for last 7 days' },
  { value: 'rolling_30d', label: 'Rolling 30 Days', description: 'Sum for last 30 days' },
  { value: 'hourly', label: 'Hourly Total', description: 'Sum for current hour' },
  { value: 'weekly', label: 'Weekly Total', description: 'Sum for current week' },
  { value: 'monthly', label: 'Monthly Total', description: 'Sum for current month' }
]

const COMPLIANCE_FIELDS = [
  { value: 'dowjones.pep_match', label: 'PEP Match', type: 'boolean' },
  { value: 'dowjones.pep_score', label: 'PEP Score', type: 'number' },
  { value: 'dowjones.sanctions_match', label: 'Sanctions Match', type: 'boolean' },
  { value: 'dowjones.watchlist_match', label: 'Watchlist Match', type: 'boolean' },
  { value: 'dowjones.risk_score', label: 'Risk Score', type: 'number' },
  { value: 'Country', label: 'Country Code', type: 'string' }
]

function RulesBuilder() {
  const { ruleId } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('basic')
  const [isEditing, setIsEditing] = useState(!!ruleId)
  const [testResults, setTestResults] = useState(null)
  const [isTestingRule, setIsTestingRule] = useState(false)
  const [fieldSearchTerm, setFieldSearchTerm] = useState('')
  const [showFieldSearch, setShowFieldSearch] = useState({})

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      type: 'transaction',
      processing_category: 'session-based',
      category: 'AML',
      severity: 'MEDIUM',
      enabled: true,
      typology: '',
      conditions: {
        type: 'and',
        conditions: [{ field: '', operator: 'equals', value: '' }]
      },
      actions: {
        alert: true,
        block_transaction: false,
        escalate_to: [],
        auto_report: false
      }
    }
  })

  const watchedType = watch('type')
  const watchedProcessingCategory = watch('processing_category')
  const watchedConditions = watch('conditions')

  useEffect(() => {
    if (ruleId) {
      // Load existing rule for editing
      loadRule(ruleId)
    }
  }, [ruleId])

  const loadRule = async (id) => {
    try {
      // TODO: Implement API call to load rule
      console.log('Loading rule:', id)
    } catch (error) {
      toast.error('Failed to load rule')
    }
  }

  const onSubmit = async (data) => {
    try {
      const ruleData = {
        ...data,
        id: isEditing ? ruleId : `${data.type.toUpperCase()}_${Date.now()}`,
        metadata: {
          tags: [],
          created_by: 'user',
          created_at: new Date().toISOString(),
          ...(data.typology && { typology: data.typology }),
          ...(isEditing && { last_modified: new Date().toISOString() })
        }
      }

      // Make API call to save rule
      const url = isEditing ? getLocalApiUrl(`/api/rules/${ruleId}`) : getLocalApiUrl('/api/rules')
      const method = isEditing ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ruleData)
      })
      
      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} rule`)
      }
      
      const result = await response.json()
      console.log('Rule saved:', result)
      
      toast.success(isEditing ? 'Rule updated successfully' : 'Rule created successfully')
      navigate('/rules')
    } catch (error) {
      toast.error('Failed to save rule')
    }
  }

  const addCondition = () => {
    const currentConditions = watchedConditions.conditions || []
    setValue('conditions.conditions', [
      ...currentConditions,
      { field: '', operator: 'equals', value: '' }
    ])
  }

  const removeCondition = (index) => {
    const currentConditions = watchedConditions.conditions || []
    setValue('conditions.conditions', currentConditions.filter((_, i) => i !== index))
  }

  const testRule = async () => {
    console.log('🔍 TEST RULE FUNCTION CALLED')
    setIsTestingRule(true)
    try {
      const formData = watch()
      
      // Sample test data based on rule type
      const testData = getTestData(formData.type)
      
      // Real API call to test rule (use TXN_001 as default for testing)
      const ruleId = formData.id || 'TXN_001'
      const testUrl = getLocalApiUrl(`/api/rules/${ruleId}/test`)
      console.log('📡 Making API call to:', testUrl, 'with data:', testData)
      const response = await fetch(testUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          testType: 'single',
          data: testData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const result = await response.json()
      
      if (result.success && result.data.results.length > 0) {
        const testResult = result.data.results[0]
        const alert = testResult.alerts?.[0]
        
        setTestResults({
          triggered: testResult.triggered,
          confidence: alert?.confidence || 0,
          processingTime: result.data.summary.processingTime || 0,
          timestamp: alert?.timestamp || new Date().toISOString(),
          details: {
            evaluatedConditions: alert?.conditionResults || []
          }
        })
      } else {
        throw new Error('No test results returned')
      }
      
      setIsTestingRule(false)
    } catch (error) {
      console.error('Error testing rule:', error)
      toast.error('Failed to test rule: ' + error.message)
      setIsTestingRule(false)
    }
  }

  const getTestData = (ruleType) => {
    switch (ruleType) {
      case 'transaction':
        return {
          CashIn: 15000,
          CashOut: 2000,
          CashDrop: 500,
          CashlessIn: 2000,
          Turnover: 18000,
          TransactionType: "CASH_IN",
          CreditWin: 1500,
          Bills: 300,
          Bill100: 5,
          Stroke: 45,
          MemberID: 'M001234',
          EgmHouseNo: 'EGM001',
          VenueCode: 'VEN001',
          TransactionPK: 'TX123456789',
          SnapshotTime: '2025-08-04T06:25:35.634Z',
          // Player Profile Data
          profile: {
            age: 35,
            type: 'member',
            isMember: true,
            isVisitor: false,
            riskScore: 75,
            membershipTier: 'Gold',
            registrationDate: '2023-01-15'
          },
          // Gaming Machine Data
          egm: {
            machineId: 'EGM001',
            machineType: 'Slot',
            denomination: 1.0,
            gameTheme: 'Buffalo Gold',
            location: 'Main Floor',
            zone: 'High Limit'
          },
          // Time of Day Data
          timeOfDay: {
            period: 'day',
            isNight: false,
            isDay: true,
            hour: 14
          }
        }
      case 'session':
        return {
          SessionPK: 'S123456789',
          MemberID: 'M001234',
          EgmName: 'Buffalo Gold',
          EgmHouseNo: 'EGM001',
          StartTime: '2024-07-17T10:00:00Z',
          EndTime: '2024-07-17T13:30:00Z',
          Turnover: 25000,
          CreditWin: 3500,
          Stroke: 120,
          SessionDuration: 210,
          Venue: 'Twinstars',
          // Sample period comparison data
          periodComparison: {
            current: {
              sessionCount: 15,
              avgTurnover: 22000,
              avgBetSize: 25.5,
              avgWinPercentage: 14.2,
              avgSessionDuration: 185,
              uniqueEGMCount: 3
            },
            previous: {
              sessionCount: 8,
              avgTurnover: 12000,
              avgBetSize: 15.8,
              avgWinPercentage: 18.1,
              avgSessionDuration: 120,
              uniqueEGMCount: 2
            },
            changes: {
              turnoverChange: 83.3,  // 83% increase
              winPercentageChange: -21.5,  // 21.5% decrease
              sessionCountChange: 87.5,  // 87.5% increase
              avgBetSizeChange: 61.4,  // 61.4% increase
              sessionDurationChange: 54.2  // 54.2% increase
            },
            hasValidComparison: true
          },
          // Sample device pattern data
          devicePatterns: {
            timeWindow: '1hour',
            uniqueDeviceCount: 3,
            sessionCount: 8,
            totalTurnover: 45000,
            devices: ['EGM001', 'EGM002', 'EGM003'],
            hasMultipleDevices: true,
            hasRapidTransitions: true,
            rapidTransitionCount: 2,
            recentTransitions: [
              {
                fromEGM: 'EGM001',
                toEGM: 'EGM002',
                timeBetweenSeconds: 45,
                isRapidTransition: true
              },
              {
                fromEGM: 'EGM002', 
                toEGM: 'EGM003',
                timeBetweenSeconds: 30,
                isRapidTransition: true
              }
            ],
            rapidTransitions: [
              {
                fromEGM: 'EGM001',
                toEGM: 'EGM002', 
                timeBetweenSeconds: 45
              },
              {
                fromEGM: 'EGM002',
                toEGM: 'EGM003',
                timeBetweenSeconds: 30
              }
            ]
          },
          // Sample time pattern data
          timePatterns: {
            currentSession: {
              startsAtOpening: true,
              endsAtClosing: false,
              spansMealTime: true,
              duringMealOnly: false,
              unusualTiming: false,
              startHour: 6,
              endHour: 15,
              isWeekend: false,
              startPeriod: 'opening',
              endPeriod: 'normal'
            },
            behaviorFlags: {
              frequentOpeningStarter: 8,
              frequentClosingEnder: 3,
              mealTimeGambler: 12,
              unusualHoursGambler: 2,
              frequentOpeningStarterPct: 80.0,
              frequentClosingEnderPct: 30.0,
              mealTimeGamblerPct: 60.0,
              unusualHoursGamblerPct: 20.0
            },
            recentSessionCount: 20,
            hasOpeningPattern: true,    // >70%
            hasClosingPattern: false,   // <70%
            hasMealTimePattern: true,   // >50%
            hasUnusualTimingPattern: false // <30%
          }
        }
      case 'cumulative':
        return {
          MemberID: 'M001234',
          daily: { totalCashIn: 28000, transactionCount: 15 },
          rolling24h: { totalAmount: 35000, count: 22 }
        }
      case 'compliance':
        return {
          MemberID: 'M001234',
          dowjones: {
            pep_match: true,
            pep_score: 85,
            sanctions_match: false,
            risk_score: 75
          }
        }
      case 'ongoing':
        return {
          eventType: 'tier_upgrade',
          MemberID: 'M001234',
          event_timestamp: '2024-07-17T14:00:00Z',
          event_data: {
            old_tier: 'Silver',
            new_tier: 'Gold',
            reason: 'automatic_upgrade'
          }
        }
      case 'payout':
        return {
          PayoutAmount: 10000,
          PayoutMethod: 'CASH',
          PayoutReason: 'jackpot_win',
          MemberID: 'M001234',
          EgmHouseNo: 'EGM001',
          CashOut: 10000,
          Turnover: 150000
        }
      default:
        return {}
    }
  }

  const filterFields = (fields, searchTerm) => {
    if (!searchTerm) return fields;
    
    const term = searchTerm.toLowerCase();
    return fields.filter(field => 
      field.label.toLowerCase().includes(term) || 
      field.value.toLowerCase().includes(term)
    );
  };

  const groupFieldsByCategory = (fields, searchTerm = '') => {
    const filteredFields = filterFields(fields, searchTerm);
    
    const grouped = {
      'Basic Fields': [],
      'Cash & Money': [],
      'Gaming Metrics': [],
      'Player Profile': [],
      'Gaming Machine': [],
      'Time of Day': [],
      'Session Analysis': [],
      'Period Comparison': [],
      'Device Patterns': [],
      'Time Patterns': [],
      'Compliance': [],
      'Advanced': []
    };

    filteredFields.forEach(field => {
      const fieldValue = field.value.toLowerCase();
      
      if (fieldValue.includes('profile.')) {
        grouped['Player Profile'].push(field);
      } else if (fieldValue.includes('egm.')) {
        grouped['Gaming Machine'].push(field);
      } else if (fieldValue.includes('timeofday.')) {
        grouped['Time of Day'].push(field);
      } else if (fieldValue.includes('cash') || fieldValue.includes('bill') || fieldValue.includes('coin') || fieldValue.includes('ticket')) {
        grouped['Cash & Money'].push(field);
      } else if (fieldValue.includes('turnover') || fieldValue.includes('credit') || fieldValue.includes('stroke') || fieldValue.includes('bet')) {
        grouped['Gaming Metrics'].push(field);
      } else if (fieldValue.includes('session') || fieldValue.includes('duration') || fieldValue.includes('egm')) {
        grouped['Session Analysis'].push(field);
      } else if (fieldValue.includes('periodcomparison') || fieldValue.includes('current') || fieldValue.includes('previous') || fieldValue.includes('change')) {
        grouped['Period Comparison'].push(field);
      } else if (fieldValue.includes('devicepatterns') || fieldValue.includes('device') || fieldValue.includes('transition')) {
        grouped['Device Patterns'].push(field);
      } else if (fieldValue.includes('timepatterns') || fieldValue.includes('opening') || fieldValue.includes('closing') || fieldValue.includes('meal')) {
        grouped['Time Patterns'].push(field);
      } else if (fieldValue.includes('dowjones') || fieldValue.includes('pep') || fieldValue.includes('sanctions') || fieldValue.includes('country')) {
        grouped['Compliance'].push(field);
      } else if (fieldValue.includes('memberid') || fieldValue.includes('venue') || fieldValue.includes('time') || fieldValue.includes('date')) {
        grouped['Basic Fields'].push(field);
      } else {
        grouped['Advanced'].push(field);
      }
    });

    // Remove empty groups
    Object.keys(grouped).forEach(key => {
      if (grouped[key].length === 0) {
        delete grouped[key];
      }
    });

    return grouped;
  };

  const getFieldsForRuleType = (ruleType, processingCategory = 'session-based') => {
    let baseFields = [];
    
    // Common fields available for all rule types
    const commonFields = [
      ...PLAYER_PROFILE_FIELDS,
      ...GAMING_MACHINE_FIELDS,
      ...TIME_OF_DAY_FIELDS
    ];
    
    // Get base fields based on rule type
    switch (ruleType) {
      case 'session':
        baseFields = [...SESSION_FIELDS, ...commonFields];
        // Add period comparison fields for cumulative session analysis
        if (processingCategory === 'cumulative') {
          baseFields.push(...PERIOD_COMPARISON_FIELDS);
        }
        // Always add device pattern fields for session rules (useful for both session-based and cumulative)
        baseFields.push(...DEVICE_PATTERN_FIELDS);
        // Add time-based pattern fields for session rules
        baseFields.push(...TIME_PATTERN_FIELDS);
        break;
      case 'onboarding':
        baseFields = [
          ...COMPLIANCE_FIELDS, // PEP, sanctions checks during onboarding
          ...PLAYER_PROFILE_FIELDS,
          { value: 'MemberID', label: 'Player ID', type: 'string' },
          { value: 'Country', label: 'Country Code', type: 'string' },
          { value: 'registration_date', label: 'Registration Date', type: 'date' }
        ];
        break;
      case 'ongoing':
        baseFields = [
          ...ONGOING_EVENT_TYPES.map(event => ({
            value: `event.${event.value}`,
            label: `Event: ${event.label}`,
            type: 'boolean'
          })),
          ...commonFields,
          { value: 'MemberID', label: 'Player ID', type: 'string' },
          { value: 'event_timestamp', label: 'Event Timestamp', type: 'datetime' },
          { value: 'event_data', label: 'Event Data', type: 'object' }
        ];
        break;
      case 'transaction':
      default:
        baseFields = [...TRANSACTION_FIELDS, ...commonFields];
        break;
    }
    
    // Add cumulative fields if processing category is cumulative
    if (processingCategory === 'cumulative') {
      const cumulativeFields = CUMULATIVE_AGGREGATIONS.map(agg => ({
        value: `${agg.value}.totalCashIn`,
        label: `${agg.label} - Total Cash In`,
        type: 'number'
      }));
      return [...baseFields, ...cumulativeFields];
    }
    
    return baseFields;
  }

  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: InformationCircleIcon },
    { id: 'conditions', name: 'Conditions', icon: ExclamationTriangleIcon },
    { id: 'actions', name: 'Actions', icon: PlayIcon },
    { id: 'test', name: 'Test', icon: PlayIcon }
  ]

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/rules')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit Rule' : 'Create New Rule'}
              </h1>
              <p className="text-sm text-gray-500">
                {isEditing ? 'Modify existing rule configuration' : 'Build a new monitoring rule'}
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={testRule}
              disabled={isTestingRule}
              className="btn-secondary flex items-center"
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              {isTestingRule ? 'Testing...' : 'Test Rule'}
            </button>
            <button
              type="submit"
              form="rule-form"
              className="btn-primary"
            >
              {isEditing ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Form */}
      <form id="rule-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="card space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Name *
                </label>
                <input
                  {...register('name', { required: 'Rule name is required' })}
                  className="input-field"
                  placeholder="Enter rule name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Type *
                </label>
                <select
                  {...register('type', { required: 'Rule type is required' })}
                  className="select-field"
                >
                  {RULE_TYPES.map((type) => (
                    <option key={type.value} value={type.value} title={type.description}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <div className="mt-2 p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-600 font-medium mb-1">Rule Type Guide:</p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li><strong>Transaction:</strong> Monitors individual transactions (bill stuffing, large amounts)</li>
                    <li><strong>Session:</strong> Analyzes gaming sessions (device patterns, time behaviors)</li>
                    <li><strong>Onboarding:</strong> Checks during registration (PEP screening, risk assessment)</li>
                    <li><strong>Ongoing:</strong> Event-triggered monitoring (eKYC requests, tier upgrades)</li>
                  </ul>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Processing Category *
                </label>
                <select
                  {...register('processing_category', { required: 'Processing category is required' })}
                  className="select-field"
                >
                  {PROCESSING_CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value} title={category.description}>
                      {category.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  How the rule analyzes data (single record vs. aggregated over time)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  {...register('category', { required: 'Category is required' })}
                  className="select-field"
                >
                  {Object.entries(RULE_CATEGORIES).map(([groupKey, categories]) => (
                    <optgroup key={groupKey} label={CATEGORY_GROUPS[groupKey]}>
                      {categories.map((category) => (
                        <option key={category.value} value={category.value} title={category.description}>
                          {category.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select the category that best describes this rule's purpose
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Severity Level *
                </label>
                <select
                  {...register('severity', { required: 'Severity is required' })}
                  className="select-field"
                >
                  {SEVERITY_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investigation Typology
                </label>
                <select {...register('typology')} className="select-field">
                  <option value="">— Not set (auto-detect from rule name) —</option>
                  {TYPOLOGY_KEYS.map((k) => (
                    <option key={k} value={k}>{TYPOLOGIES[k]?.label || k}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Links rule to SVCP decision tree when creating investigation cases
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="input-field"
                placeholder="Describe what this rule detects..."
              />
            </div>

            {watchedProcessingCategory === 'cumulative' && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center mb-3">
                  <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <h4 className="text-sm font-medium text-blue-900">Cumulative Analysis Settings</h4>
                </div>
                <p className="text-xs text-blue-700 mb-3">
                  Cumulative rules analyze patterns over time periods and can access period comparison, device patterns, and time-based behavioral data.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">
                      Time Window
                    </label>
                    <select
                      {...register('aggregation.timeWindow')}
                      className="select-field"
                    >
                      {CUMULATIVE_AGGREGATIONS.map((agg) => (
                        <option key={agg.value} value={agg.value}>
                          {agg.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">
                      Group By
                    </label>
                    <select
                      {...register('aggregation.groupBy')}
                      className="select-field"
                    >
                      <option value="member">Member</option>
                      <option value="egm">EGM</option>
                      <option value="venue">Venue</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                {...register('enabled')}
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Enable this rule
              </label>
            </div>
          </div>
        )}

        {/* Conditions Tab */}
        {activeTab === 'conditions' && (
          <div className="card space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Rule Conditions</h3>
              <div className="flex items-center space-x-4">
                <select
                  {...register('conditions.type')}
                  className="select-field w-auto"
                >
                  <option value="and">All conditions must match (AND)</option>
                  <option value="or">Any condition can match (OR)</option>
                </select>
                <button
                  type="button"
                  onClick={addCondition}
                  className="btn-secondary flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Condition
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {watchedConditions.conditions?.map((condition, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      {/* Field Search Input */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search fields..."
                          value={showFieldSearch[index] ? fieldSearchTerm : ''}
                          onChange={(e) => setFieldSearchTerm(e.target.value)}
                          onFocus={() => setShowFieldSearch(prev => ({ ...prev, [index]: true }))}
                          onBlur={() => setTimeout(() => setShowFieldSearch(prev => ({ ...prev, [index]: false })), 200)}
                          className="input-field text-sm"
                        />
                        {showFieldSearch[index] && fieldSearchTerm && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                            {(() => {
                              const fields = getFieldsForRuleType(watchedType, watchedProcessingCategory);
                              const filteredFields = filterFields(fields, fieldSearchTerm);
                              return filteredFields.map((field) => (
                                <div
                                  key={field.value}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                  onClick={() => {
                                    setValue(`conditions.conditions.${index}.field`, field.value);
                                    setFieldSearchTerm('');
                                    setShowFieldSearch(prev => ({ ...prev, [index]: false }));
                                  }}
                                >
                                  <div className="font-medium">{field.label}</div>
                                  <div className="text-xs text-gray-500">{field.value}</div>
                                </div>
                              ));
                            })()}
                          </div>
                        )}
                      </div>
                      
                      {/* Traditional Dropdown */}
                      <select
                        {...register(`conditions.conditions.${index}.field`)}
                        className="select-field"
                      >
                        <option value="">Select field...</option>
                        {(() => {
                          const fields = getFieldsForRuleType(watchedType, watchedProcessingCategory);
                          const groupedFields = groupFieldsByCategory(fields);
                          
                          return Object.entries(groupedFields).map(([category, categoryFields]) => (
                            <optgroup key={category} label={category}>
                              {categoryFields.map((field) => (
                                <option key={field.value} value={field.value} title={field.description || field.label}>
                                  {field.label}
                                </option>
                              ))}
                            </optgroup>
                          ));
                        })()}
                      </select>
                    </div>
                    <div>
                      <select
                        {...register(`conditions.conditions.${index}.operator`)}
                        className="select-field"
                      >
                        {OPERATORS.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <input
                        {...register(`conditions.conditions.${index}.value`)}
                        className="input-field"
                        placeholder="Value"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCondition(index)}
                    className="p-2 text-red-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {watchedConditions.conditions?.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No conditions defined. Add at least one condition to continue.</p>
              </div>
            )}
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
          <div className="card space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Rule Actions</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700">Alert Actions</h4>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      {...register('actions.alert')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Generate Alert</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.auto_report')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Auto Generate Report</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.create_case')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Create Investigation Case</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.flag_record')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Flag Record</span>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700">Case Types</h4>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      {...register('actions.case_type')}
                      type="radio"
                      value="ECDD"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">ECDD (Enhanced Customer Due Diligence)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.case_type')}
                      type="radio"
                      value="eKYC"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">eKYC (Enhanced Know Your Customer)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.case_type')}
                      type="radio"
                      value="re-eKYC"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">re-eKYC (Re-enhanced Know Your Customer)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.case_type')}
                      type="radio"
                      value="UAR"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">UAR (Unusual Activity Report)</span>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700">Blocking Actions</h4>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      {...register('actions.block_transaction')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Block Transaction</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.block_account')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Block Account</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('actions.require_manual_review')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Require Manual Review</span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Escalation Teams
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {['AML_TEAM', 'SECURITY_TEAM', 'COMPLIANCE_TEAM'].map((team) => (
                  <label key={team} className="flex items-center">
                    <input
                      {...register('actions.escalate_to')}
                      type="checkbox"
                      value={team}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">
                      {team.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Test Tab */}
        {activeTab === 'test' && (
          <div className="card space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Test Rule</h3>
              <div className="flex space-x-2">
                <select className="select-field text-sm">
                  <option value="single">Single Record</option>
                  <option value="bulk">Bulk Test Data</option>
                  <option value="custom">Custom Dataset</option>
                </select>
                <button
                  type="button"
                  onClick={testRule}
                  disabled={isTestingRule}
                  className="btn-primary flex items-center"
                >
                  <PlayIcon className="h-4 w-4 mr-2" />
                  {isTestingRule ? 'Testing...' : 'Run Test'}
                </button>
              </div>
            </div>

            {isTestingRule && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600">Testing rule...</span>
              </div>
            )}

            {testResults && (
              <div className={`p-4 rounded-lg ${
                testResults.triggered ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center mb-3">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    testResults.triggered ? 'bg-red-500' : 'bg-green-500'
                  }`}></div>
                  <h4 className={`font-medium ${
                    testResults.triggered ? 'text-red-900' : 'text-green-900'
                  }`}>
                    Rule {testResults.triggered ? 'Triggered' : 'Not Triggered'}
                  </h4>
                  <span className={`ml-auto text-sm ${
                    testResults.triggered ? 'text-red-700' : 'text-green-700'
                  }`}>
                    Confidence: {testResults.confidence}%
                  </span>
                </div>
                
                {testResults.details?.evaluatedConditions && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-gray-700">Condition Results:</h5>
                    {testResults.details.evaluatedConditions.map((cond, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          {cond.condition.field} {cond.condition.operator} {cond.condition.value}
                        </span>
                        <div className="flex items-center">
                          <span className="text-gray-500 mr-2">
                            Actual: {cond.actualValue}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            cond.result ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {cond.result ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Test Data</h4>
              <pre className="text-xs text-gray-600 overflow-x-auto">
                {JSON.stringify(getTestData(watchedType), null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Save/Cancel Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/rules')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
          >
            {isEditing ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default RulesBuilder
